import { requireApiHost } from '@/service/api_host'
import { ApiError, type ApiErrorKind } from './api_error'

interface ApiResponse<T> {
  code?: number | string
  data?: T
  error?: string
}

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  query?: Record<string, unknown>
  body?: unknown
  headers?: Record<string, string>
  timeoutMs?: number
}

let authExpiredHandler: ((error: ApiError) => void | Promise<void>) | null = null

export const setAuthExpiredHandler = (handler: ((error: ApiError) => void | Promise<void>) | null) => {
  authExpiredHandler = handler
}

const errorKindForStatus = (status: number): ApiErrorKind => {
  if (status === 401) return 'AUTH_EXPIRED'
  if (status === 403) return 'FORBIDDEN'
  if (status === 404) return 'NOT_FOUND'
  if (status === 409) return 'CONFLICT'
  if (status === 422) return 'VALIDATION'
  if (status >= 500) return 'SERVER'
  return 'UNKNOWN'
}

const notifyAuthExpired = async (error: ApiError) => {
  if (error.kind !== 'AUTH_EXPIRED' || !authExpiredHandler) return
  try {
    await authExpiredHandler(error)
  } catch (cause) {
    console.warn('auth expired handler failed', cause)
  }
}

const serverMessageOf = (payload: unknown): string | undefined => {
  if (!payload || typeof payload !== 'object') return undefined
  const item = payload as Record<string, unknown>
  return typeof item.error === 'string' && item.error.trim() ? item.error.trim() : undefined
}

const isAuthFailure = (error: string | undefined): boolean => /^\s*\[401\]\s*/i.test(error || '')

const buildUrl = (path: string, query?: Record<string, unknown>): string => {
  let host: string
  try {
    host = requireApiHost()
  } catch (cause) {
    throw new ApiError('CONFIG', 'API host is not configured', { cause })
  }

  const queryString = new URLSearchParams()
  Object.entries(query || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') queryString.set(key, String(value))
  })
  return `${host}${path}${queryString.size ? `?${queryString.toString()}` : ''}`
}

export const apiRequest = async <T>(path: string, options: ApiRequestOptions = {}): Promise<T> => {
  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), options.timeoutMs ?? 30_000)
  let response: Response

  try {
    response = await fetch(buildUrl(path, options.query), {
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: controller.signal,
    })
  } catch (cause) {
    if (cause instanceof ApiError) throw cause
    if (cause instanceof DOMException && cause.name === 'AbortError') {
      throw new ApiError('TIMEOUT', `API request timed out: ${path}`, { cause })
    }
    throw new ApiError('NETWORK', `API request failed: ${path}`, { cause })
  } finally {
    window.clearTimeout(timeout)
  }

  const text = await response.text()
  let payload: ApiResponse<T> | T | undefined
  let parseError: unknown
  if (text) {
    try {
      payload = JSON.parse(text) as ApiResponse<T> | T
    } catch (cause) {
      parseError = cause
    }
  }

  const serverMessage = serverMessageOf(payload)
  if (!response.ok) {
    const error = new ApiError(errorKindForStatus(response.status), `API request failed with status ${response.status}: ${path}`, {
      status: response.status,
      serverMessage,
      cause: parseError,
    })
    await notifyAuthExpired(error)
    throw error
  }

  if (parseError) {
    throw new ApiError('INVALID_RESPONSE', `API returned invalid JSON: ${path}`, {
      status: response.status,
      cause: parseError,
    })
  }

  if (payload && typeof payload === 'object' && 'code' in payload) {
    const wrapped = payload as ApiResponse<T>
    if (wrapped.code !== 0 && wrapped.code !== '0') {
      const serverMessage = serverMessageOf(wrapped)
      const kind = isAuthFailure(serverMessage) ? 'AUTH_EXPIRED' : 'BUSINESS'
      const error = new ApiError(kind, `API business request failed: ${path}`, {
        status: response.status,
        businessCode: wrapped.code,
        serverMessage,
      })
      await notifyAuthExpired(error)
      throw error
    }
    return wrapped.data as T
  }

  return payload as T
}
